/**
 * What the centre `+` opens.
 *
 * It is an OVERLAY, not a route — deliberately, and for two reasons. Wallet's
 * add screen is modal (you return to exactly where you were), and adding a
 * `/entry` route would mean adding a route the `PAGE_REGISTRY` does not know,
 * which is precisely the thing that has no permission check. So the sheet lives
 * inside whatever guarded route you are already on.
 *
 * FALLBACK: until `frontend/web/src/wallet/entry/` exists, this renders the app's
 * existing `<QuickAddSheet/>` — the same amount pad, category picker and
 * account picker the current shell's FAB opens. The centre button therefore
 * works from the first commit of this shell, and swapping in the Wallet entry
 * screen later changes nothing else.
 */
import { Suspense, useEffect } from "react";
import { DesktopEntry } from "@/wallet/entry/DesktopEntry";
import { useIsDesktop } from "./useIsDesktop";
import { QuickAddSheet } from "@/components/quick-add/QuickAddSheet";
import { Sheet } from "./Sheet";
import { useWalletShell } from "./context";
import { EntryScreen } from "./screens";

function EntryFallback() {
  return (
    <div className="flex h-40 items-center justify-center">
      <span
        aria-hidden="true"
        className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--cp-line)] border-t-[var(--cp-accent)]"
      />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export function EntryOverlay() {
  const { entryOpen, entryType, entryClone, entryEdit, closeEntry } = useWalletShell();
  const isDesktop = useIsDesktop();

  // Desktop: a wide MDM-style form, not the phone keypad in a 480px panel
  // (owner, 2026-09-26: "pop-up is not working properly").
  if (isDesktop) {
    return entryOpen ? (
      <DesktopEntryModal
        key={entryEdit ? `edit-${entryEdit.id}` : (entryClone?.id ?? "new")}
        record={entryEdit ?? undefined}
        onClose={closeEntry}
        defaultType={entryType}
        cloneFrom={entryClone ?? undefined}
      />
    ) : null;
  }

  if (!EntryScreen) {
    // The classic sheet has no transfer form; it opens on expense instead.
    return (
      <QuickAddSheet
        open={entryOpen}
        onClose={closeEntry}
        defaultType={entryType === "transfer" ? "expense" : entryType}
      />
    );
  }

  return (
    <Sheet open={entryOpen} onClose={closeEntry} title="New record" variant="full" bareHeader>
      <Suspense fallback={<EntryFallback />}>
        {/* Keyed on the clone source so a second Clone starts a fresh screen. */}
        <EntryScreen
          key={entryEdit ? `edit-${entryEdit.id}` : (entryClone?.id ?? "new")}
          record={entryEdit ?? undefined}
          onClose={closeEntry}
          defaultType={entryType}
          cloneFrom={entryClone ?? undefined}
        />
      </Suspense>
    </Sheet>
  );
}

function DesktopEntryModal({
  record,
  onClose,
  defaultType,
  cloneFrom,
}: {
  record?: Parameters<typeof DesktopEntry>[0]["record"];
  onClose: () => void;
  defaultType: Parameters<typeof DesktopEntry>[0]["defaultType"];
  cloneFrom: Parameters<typeof DesktopEntry>[0]["cloneFrom"];
}) {
  // Escape closes; the backdrop deliberately does NOT — a stray click beside
  // a half-typed record should not throw it away.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);
  return (
    <div className="dxe-backdrop">
      <DesktopEntry
        record={record}
        onClose={onClose}
        defaultType={defaultType}
        cloneFrom={cloneFrom}
        // "Save" finishes; "Save & add another" keeps the form for the next one.
        onSaved={(saved) => {
          if (!saved.keepOpen) onClose();
        }}
      />
    </div>
  );
}
